import { AvailableTabs } from '@/store/fundDetails/typings';
import React, { useEffect, useMemo, useState } from 'react';
import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import SecondLevelTabs from 'components/Generic/SecondLevelTabs';

type TabComponentsMap = Record<string, React.ComponentType>;

type TabTitlesMap = Record<string, string>;

type Props = {
  availableTabs: AvailableTabs;
  tabComponentsByTabKeys: TabComponentsMap;
  tabTitlesByTabKeys: TabTitlesMap;
  tabLvl?: string;
  className?: string;
  tabData?: any;
};

const FundDetailsSecondLevelTabGroup = ({
  availableTabs,
  tabComponentsByTabKeys,
  tabTitlesByTabKeys,
  tabLvl = 'subTab',
  className,
  tabData,
}: Props) => {
  const { updateUrlParams } = useHelpers();
  const getInitialTabKey = (): string => {
    const selectedTab = updateUrlParams()[tabLvl];
    return selectedTab || '';
  };
  const [activeTabKey, setActiveTabKey] = useState(getInitialTabKey);
  const { t } = useI18n();

  const tabs = useMemo(() => {
    if (!availableTabs) return [];
    let tabsToShow: any = Object.entries(availableTabs);
    tabsToShow = tabsToShow.filter(
      ([key, value]: [string, string]) => value && tabTitlesByTabKeys[key]
    );
    tabsToShow = tabsToShow.map(([key]: string) => ({ key, title: tabTitlesByTabKeys[key] }));
    return tabsToShow;
  }, [availableTabs, tabTitlesByTabKeys]);

  const ActiveTabComponent = useMemo(() => {
    return tabComponentsByTabKeys[activeTabKey];
  }, [activeTabKey, tabComponentsByTabKeys]);

  const setActiveTab = (tabKey: string) => {
    setActiveTabKey(tabKey);
    updateUrlParams([{ name: tabLvl, value: tabKey }]);
  };

  useEffect(() => {
    const selectedSecondLvlTab = updateUrlParams()[tabLvl];
    const availableTabs: Array<{ key: string; value: string }> = tabs.map(
      (tab: { key: string }) => tab.key
    );

    if (availableTabs.includes(selectedSecondLvlTab)) {
      setActiveTabKey(selectedSecondLvlTab);
    } else if (!tabs.length) {
      updateUrlParams([{ name: tabLvl, value: '' }]);
    } else {
      setActiveTabKey(tabs[0]?.key);
      updateUrlParams([{ name: tabLvl, value: tabs[0]?.key }]);
    }
  }, [updateUrlParams, tabs, tabLvl]);

  return (
    tabs.length !== 0 && (
      <div className={className}>
        <SecondLevelTabs activeTabKey={activeTabKey} tabs={tabs} tabClick={setActiveTab} />

        {ActiveTabComponent ? (
          <ActiveTabComponent
            data-testid={tabComponentsByTabKeys[activeTabKey]}
            tabData={tabData}
            selectedTab={activeTabKey}
          />
        ) : (
          <FundDetailsTabRow className="mt-4">
            <div className="body-default">{t('noResultsMessage')}</div>
          </FundDetailsTabRow>
        )}
      </div>
    )
  );
};

export default FundDetailsSecondLevelTabGroup;
